/**
 * Credit Service — a store's client credits (money a store can put toward future orders).
 *
 * GET /storefront/credits?clientId= returns { enabled, availableTotal, credits[] }. `enabled` is false
 * when the store's company doesn't use client credits; the Credits page, nav link and alert all key
 * off it. Customers never see a credit's reason or voided credits — the API leaves them out.
 *
 * GET /storefront/orders/{id}/credits returns the credits applied to one of the customer's orders,
 * one line each ({ brandId, brandName, amount, dateApplied }), for the order summary and invoice.
 */
import { apiService } from "./api.js";

class CreditService {
  async getStoreCredits(clientId) {
    return apiService.get("/storefront/credits", { clientId });
  }

  async getOrderCredits(orderId) {
    return apiService.get(`/storefront/orders/${orderId}/credits`);
  }
}

/** An applied credit's label by type: the brand's name, Stash House or Overpay. */
export const creditLineLabel = (line) => {
  if (line.creditTypeId === 3) return "Overpay";
  if (line.creditTypeId === 2 || line.brandId == null) return "Stash House";
  return line.brandName || "Credit";
};

/** What a credit can be used on, by type: its brand's products, house-brand products, or anything. */
export const creditScopeLabel = (credit) => {
  if (credit.creditTypeId === 3) return "Any product";
  if (credit.creditTypeId === 2 || credit.brandId == null) return "House-brand products only";
  return `${credit.brandName} products only`;
};

/**
 * "Stash House credits can only be used on house-brand products: Crumble, Drool and NOSHY." — the
 * house brands the store's company carries. Null when there are none.
 */
export const stashHouseBrandsText = (brands = []) => {
  const names = brands.map((b) => b.brandName).filter(Boolean);
  if (names.length === 0) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Stash House credits can only be used on house-brand products: ${list}.`;
};

const creditService = new CreditService();
export default creditService;
